import { initializeApp } from 'firebase/app'
import {
  getFirestore,
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  type QuerySnapshot,
  type DocumentData,
  connectFirestoreEmulator,
} from 'firebase/firestore'
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  listAll,
  deleteObject,
  connectStorageEmulator,
} from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { getAuth, connectAuthEmulator } from 'firebase/auth'
import environment, { STORAGE_JOBS_FOLDER } from './config/environment'
import { useLogger } from './composables/useLogger'

const logger = useLogger()

logger.info('Inicializando Firebase', {
  component: 'Firebase',
  method: 'initialize',
  data: {
    projectId: environment.firebase.projectId,
    isDevelopment: environment.isDevelopment,
    useEmulator: environment.useEmulator,
  },
})

const constructApp = () => {
  try {
    const app = initializeApp(environment.firebase)
    const db = getFirestore(app)
    const storage = getStorage(app)
    const functions = getFunctions(app)
    const auth = getAuth(app)

    // Conectar a emuladores en desarrollo
    if (environment.useEmulator) {
      logger.info('Conectando a emuladores', {
        component: 'Firebase',
        method: 'initialize',
      })
      connectFirestoreEmulator(db, '127.0.0.1', 8081)
      connectStorageEmulator(storage, '127.0.0.1', 9199)
      connectFunctionsEmulator(functions, '127.0.0.1', 5001)
      connectAuthEmulator(auth, 'http://127.0.0.1:9099')
    }

    return {
      firestore: () => ({
        collection: (path: string) => {
          logger.debug('Accediendo a colección', {
            component: 'Firebase',
            method: 'firestore.collection',
            data: { path },
          })
          return {
            get: () => getDocs(collection(db, path)),
            doc: (id: string) => ({
              update: (data: DocumentData) => updateDoc(doc(db, path, id), data),
              delete: () => deleteDoc(doc(db, path, id)),
            }),
            add: (data: DocumentData) => addDoc(collection(db, path), data),
            onSnapshot: (
              callback: (snapshot: QuerySnapshot<DocumentData>) => void,
              errorCallback?: (error: Error) => void,
            ) => {
              logger.debug('Configurando onSnapshot', {
                component: 'Firebase',
                method: 'firestore.collection.onSnapshot',
                data: { path },
              })
              return onSnapshot(
                collection(db, path),
                snapshot => {
                  logger.debug('Snapshot recibido', {
                    component: 'Firebase',
                    method: 'firestore.collection.onSnapshot',
                    data: { path, size: snapshot.size },
                  })
                  callback(snapshot)
                },
                error => {
                  logger.error('Error en onSnapshot', {
                    component: 'Firebase',
                    method: 'firestore.collection.onSnapshot',
                    data: { path, error },
                  })
                  if (errorCallback) errorCallback(error)
                },
              )
            },
          }
        },
      }),
      storage: () => ({
        listAll: () => listAll(ref(storage, STORAGE_JOBS_FOLDER)),
        uploadFile: (path: string, blob: Blob, metadata?: Record<string, unknown>) =>
          uploadBytes(ref(storage, `${STORAGE_JOBS_FOLDER}/${path}`), blob, metadata),
        getDownloadURL: (path: string) =>
          getDownloadURL(ref(storage, `${STORAGE_JOBS_FOLDER}/${path}`)),
        deleteFile: (path: string) => deleteObject(ref(storage, `${STORAGE_JOBS_FOLDER}/${path}`)),
      }),
      functions: () => functions,
      auth: () => auth,
    }
  } catch (error) {
    logger.error('Error al inicializar Firebase', {
      component: 'Firebase',
      method: 'initialize',
      data: { error: error instanceof Error ? error.message : String(error) },
    })
    throw error
  }
}

export const firebase = constructApp()
